import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import EntryTypeModal from "@/components/EntryTypeModal";
import AppHeader from "@/components/AppHeader";
import BottomNavigation from "@/components/BottomNavigation";
import { useToast } from "@/hooks/use-toast";
import { entriesAPI } from "@/api/entries";
import { useAuth } from "@/contexts/AuthContext";
import { uploadFile } from "@/lib/storage-adapter";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EntryType } from "@/types";

const NewEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as { from?: string; quickAction?: boolean } | null;
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // State for upload progress
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Extract projectId from URL if available
  const [initialData, setInitialData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);

  const returnToPreviousScreen = () => {
    if (navigationState?.from) {
      navigate(navigationState.from, { replace: true });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  };
  
  useEffect(() => {
    // Always ensure the modal is open when navigating to this page
    setIsModalOpen(true);
    
    const queryParams = new URLSearchParams(location.search);
    const projectId = queryParams.get('projectId');
    const type = queryParams.get('type');

    const allowedTypes: EntryType[] = ["expense", "givenCheck", "receivedCheck", "laborCost", "delivery", "sitePhoto", "sale"];
    const safeType = type && allowedTypes.includes(type as EntryType) ? (type as EntryType) : undefined;

    if (projectId || safeType) {
      setInitialData({
        ...(projectId ? { projectId } : {}),
        ...(safeType ? { type: safeType } : {}),
      });
      return;
    }

    setInitialData(null);
  }, [location.search]);

  // Listen for reset-entry-selection event
  useEffect(() => {
    const handleResetEntrySelection = () => {
      
      // Keep modal open but reset any state
      setIsModalOpen(true);
      setInitialData(null);
      // Reset upload state if any
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('idle');
      setUploadError(null);
    };

    window.addEventListener('reset-entry-selection', handleResetEntrySelection);
    
    return () => {
      window.removeEventListener('reset-entry-selection', handleResetEntrySelection);
    };
  }, []);

  const handleEntrySubmit = async (data: any) => {
    
    
    
    try {
      // Extract callback functions if present
      const onUploadComplete = data.onUploadComplete;
      
      // Validate required fields
      if (!data.type) {
        throw new Error("Entry type is required");
      }
      if (!data.date) {
        throw new Error("Date is required");
      }
      if (!data.projectId) {
        throw new Error("Project is required");
      }
      
      // Validate amount for expense and labor cost entries
      if ((data.type === 'expense' || data.type === 'laborCost') && 
          (!data.amount || isNaN(parseFloat(data.amount)))) {
        
        throw new Error("Valid amount is required for expenses and labor costs");
      }
      
      // If there is an amount, convert it to a number
      if (data.amount) {
        data.amount = parseFloat(data.amount);
      }
      
      // Upload image if present
      let imageUrl = '';
      let uploadComplete = false;
      
      // Check for imageFile from expense form or from other entry types
      if (data.imageFile) {
        
        
        // Keep modal open during upload
        setIsModalOpen(true);
        setIsUploading(true);
        setUploadProgress(0);
        setUploadStatus('uploading');
        setUploadError(null);
        
        
        
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const newProgress = prev < 92 ? prev + (Math.random() * 3) : prev;
            
            return newProgress;
          });
        }, 200);
        
        try {
          
          
          // Create a unique path for the file
          const timestamp = Date.now();
          const path = `projects/${data.projectId}/${timestamp}`;
          
          // Use uploadFile from storage-adapter which is configured to use AWS S3
          imageUrl = await uploadFile(
            data.imageFile, 
            'project-images', 
            path
          );
          
          
          // Mark upload as complete
          uploadComplete = true;
          
          
          // Complete the progress bar
          clearInterval(progressInterval);
          setUploadProgress(100);
          setUploadStatus('success');
          
          
          // Give a small delay before continuing to show completion state
          
          await new Promise(resolve => setTimeout(resolve, 2000));
          
        } catch (uploadError) {
          // Clear the progress interval on error
          clearInterval(progressInterval);
          
          
          setUploadStatus('error');
          setUploadError(t('uploads.upload_error_occurred'));
          
          
          toast({
            title: t('uploads.image_upload_failed'),
            description: t('uploads.try_again_or_different_image'),
            variant: "destructive",
          });
          
          
          // We'll continue without an image rather than failing the entire form submission
          imageUrl = '';
          
          
          // Give time to see the error before proceeding
          
          await new Promise(resolve => setTimeout(resolve, 2000));
          
        } finally {
          
          setIsUploading(false);
        }
      }
      
      
      
      // Prepare data for API call
      const entryData = {
        type: data.type,
        date: data.date,
        due_date: data.dueDate,
        project_id: data.projectId,
        person_id: data.personId,
        description: data.description || "",
        amount: data.amount,
        image_url: imageUrl,
        created_by: user.id,
        currency: data.currency,
        original_currency: data.original_currency,
        original_amount: data.original_amount || data.amount,
        is_official: data.hasInvoice,
        payment_method: data.paymentMethod,
        advance_sub_method: data.advanceSubMethod
      };
      
      
      
      // Call the API to save the entry
      
      const result = await entriesAPI.createEntry(entryData);
      
      
      // If onUploadComplete callback was provided, call it
      if (uploadComplete && onUploadComplete && typeof onUploadComplete === 'function') {
        
        onUploadComplete();
        return; // Modal will handle navigation
      }
      
      // Show success toast
      toast({
        title: t('entries.entry_created_successfully'),
        description: t('entries.redirecting_to_home'),
      });
      
      
      // Verify upload completed if there was an image
      if (data.imageFile && !uploadComplete) {
        
      } else if (data.imageFile) {
        
      }
      
      // After a brief delay, close modal and navigate back to home
      
      setTimeout(() => {
        
        setIsModalOpen(false);
        returnToPreviousScreen();
      }, 3000);
      
    } catch (error) {
      
      
      let errorMessage = t('entries.entry_create_error');
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      }
      
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Render the upload status UI
  const renderUploadStatus = () => {
    if (!isUploading && uploadStatus === 'idle') return null;

    return (
      <div className="w-full max-w-3xl mb-4 px-4 pt-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
          <h3 className="text-[13px] font-semibold text-gray-900 text-center">{t('uploads.file_upload_status')}</h3>

          {uploadStatus === 'uploading' && (
            <>
              <div className="flex items-center text-[13px] text-gray-600">
                <Loader2 className="h-4 w-4 mr-2 animate-spin text-brand-600" />
                <span>{t('uploads.uploading_file', { progress: Math.round(uploadProgress) })}</span>
              </div>
              <Progress value={uploadProgress} className="w-full h-2" />
              <p className="text-[11px] text-center text-gray-500">{t('uploads.wait_until_page_close')}</p>
            </>
          )}

          {uploadStatus === 'success' && (
            <div className="flex items-center justify-center text-success-600 py-2">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              <span className="text-[13px] font-medium">{t('uploads.file_uploaded_successfully')}</span>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="flex items-center justify-center text-error-600 py-2">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span className="text-[13px] font-medium">{uploadError || t('uploads.file_upload_error')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle modal close - don't auto-navigate to home to allow EntryTypeModal to handle its own navigation
  const handleModalClose = () => {
    
    
    
    setIsModalOpen(false);
    returnToPreviousScreen();
  };

  return (
    <div className="page-container bg-gray-25 min-h-screen">
      <AppHeader title={t('entries.new_entry')} />

      <div className="main-content flex items-start justify-center flex-col">
        {renderUploadStatus()}

        <div className="w-full max-w-3xl pb-24">
          <EntryTypeModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onSubmit={handleEntrySubmit}
            initialData={initialData}
            uploadProgress={uploadProgress}
            uploadStatus={uploadStatus}
            isUploading={isUploading}
          />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default NewEntryPage;
